import { useState, useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router";
import { useGroupStore } from "@/stores/groupStore";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { trialEndsLabel } from "@/lib/trial";
import type { Subscription } from "@/types";

interface Prompt {
  message: string;
  action: string;
}

const TRIAL_WARNING_DAYS = 7;

/**
 * One prompt per billing state, most restrictive first. Each message has to be
 * true for the group reading it and name the step that actually resolves it.
 */
function resolvePrompt(subscriptions: Subscription[], needsUpgrade: boolean): Prompt | null {
  // Status is the only source of truth for liveness, not a billing period
  // that merely rolled.
  const active = subscriptions.find((s) => s.isActive) ?? null;

  // "Suspended" is a suspended row AND no active row, matching the backend's
  // Group::scopeSuspended(), not merely the presence of a suspended row. A
  // group that was suspended, resubscribed and paid keeps the old suspended
  // row forever; reading that alone told every member of a fully paid-up
  // group to settle invoices, permanently, and outranked every other prompt.
  //
  // It outranks everything else: it is the only one of these that describes
  // access that is actually restricted right now, and the copy has to say what
  // still works.
  if (!active && subscriptions.some((s) => s.isSuspended)) {
    return {
      message:
        "This group is suspended for unpaid invoices. You can still record savings withdrawals and loan repayments. Settle the outstanding invoices to restore full access.",
      action: "Settle Invoices",
    };
  }

  if (needsUpgrade) {
    return {
      message:
        "Your group has exceeded the member limit for its current plan. Upgrade your plan to continue using all features.",
      action: "Upgrade Plan",
    };
  }

  if (!active) {
    const pending = subscriptions.find((s) => s.isPendingPayment);
    if (pending) {
      const plan = pending.pricingTier?.name ? `${pending.pricingTier.name} Plan` : "plan";
      return {
        message: `Your ${plan} is waiting for payment. Pay the invoice to activate it.`,
        action: "Pay Invoice",
      };
    }

    // Canceled or expired with nothing live. Not necessarily a trial: a paid
    // subscription that was canceled lands here too.
    if (subscriptions.length > 0) {
      return {
        message: "Your subscription has ended. Choose a plan to restore access to all features.",
        action: "Choose Plan",
      };
    }

    return null;
  }

  // A trial runs on the plan the group already chose, so nothing needs
  // upgrading when it ends: billing for that plan simply starts. A free plan
  // has nothing to start billing for.
  if (
    active.isOnTrial &&
    active.trialEndsAt &&
    active.trialDaysRemaining <= TRIAL_WARNING_DAYS &&
    !active.pricingTier?.isFree
  ) {
    const plan = active.pricingTier?.name ? `${active.pricingTier.name} Plan` : "plan";
    return {
      message: `Your free trial ends ${trialEndsLabel(active.trialEndsAt)}. Billing for your ${plan} starts after that.`,
      action: "View Plan",
    };
  }

  return null;
}

export function UpgradePromptBanner() {
  const activeGroup = useGroupStore((s) => s.activeGroup);
  const subscriptionRevision = useGroupStore((s) => s.subscriptionRevision);
  const navigate = useNavigate();
  // Kept with the group it was fetched for. Without that, switching groups (or
  // a failed fetch) left the previous group's billing state on screen until a
  // reload.
  const [loaded, setLoaded] = useState<{ groupUuid: string; subscriptions: Subscription[] } | null>(null);

  useEffect(() => {
    if (!activeGroup?.uuid) return;
    let cancelled = false;
    api
      .get<{ data: Subscription[] }>(`/subscriptions/groups/${activeGroup.uuid}`)
      .then(({ data }) => {
        if (!cancelled) setLoaded({ groupUuid: activeGroup.uuid, subscriptions: data.data });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [activeGroup?.uuid, subscriptionRevision]);

  const subscriptions = loaded && loaded.groupUuid === activeGroup?.uuid ? loaded.subscriptions : [];
  const prompt = resolvePrompt(subscriptions, !!activeGroup?.needsUpgrade);

  if (!prompt) return null;

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 text-amber-800">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <p className="text-sm font-medium">{prompt.message}</p>
        </div>
        <Button
          size="sm"
          className="flex-shrink-0 bg-amber-600 hover:bg-amber-700 text-white"
          onClick={() => navigate("/settings?tab=subscription")}
        >
          {prompt.action}
        </Button>
      </div>
    </div>
  );
}
